import { LineTileShape, LineTileSize } from '@/constants/common'

type TileShapeConfig = {
  w: number
  h: number
  tilesPerView: number
  slidesOffsetAfter: number
  focusedTileScale: number
}

type TilesPerViewConfig = {
  [size in LineTileSize]: {
    [shape in LineTileShape]: TileShapeConfig
  }
}

const CONTAINER_WIDTH = 1720
const GAP = 24

const baseDimensions: Record<
  LineTileSize,
  Record<LineTileShape, { w: number; h: number }>
> = {
  [LineTileSize.LARGE]: {
    [LineTileShape.ASPECT16x9]: { w: 591, h: 324 },
    [LineTileShape.ASPECT2x3]: { w: 324, h: 486 },
    [LineTileShape.ASPECT9x16]: { w: 324, h: 576 },
    [LineTileShape.SQUARE]: { w: 324, h: 324 },
    [LineTileShape.CIRCLE]: { w: 324, h: 324 },
  },
  [LineTileSize.DEFAULT]: {
    [LineTileShape.ASPECT16x9]: { w: 384, h: 216 },
    [LineTileShape.ASPECT2x3]: { w: 216, h: 324 },
    [LineTileShape.ASPECT9x16]: { w: 216, h: 384 },
    [LineTileShape.SQUARE]: { w: 216, h: 216 },
    [LineTileShape.CIRCLE]: { w: 216, h: 216 },
  },
  [LineTileSize.SMALL]: {
    [LineTileShape.ASPECT16x9]: { w: 220, h: 124 },
    [LineTileShape.ASPECT2x3]: { w: 124, h: 186 },
    [LineTileShape.ASPECT9x16]: { w: 124, h: 220 },
    [LineTileShape.SQUARE]: { w: 124, h: 124 },
    [LineTileShape.CIRCLE]: { w: 124, h: 124 },
  },
  [LineTileSize.XSMALL]: {
    [LineTileShape.ASPECT16x9]: { w: 140, h: 78 },
    [LineTileShape.ASPECT2x3]: { w: 96, h: 144 },
    [LineTileShape.ASPECT9x16]: { w: 96, h: 171 },
    [LineTileShape.SQUARE]: { w: 96, h: 96 },
    [LineTileShape.CIRCLE]: { w: 96, h: 96 },
  },
}

function generateTilesPerView(): TilesPerViewConfig {
  const config = {} as TilesPerViewConfig

  for (const sizeKey in baseDimensions) {
    const size = sizeKey as LineTileSize
    config[size] = {} as Record<LineTileShape, TileShapeConfig>

    for (const shapeKey in baseDimensions[size]) {
      const shape = shapeKey as LineTileShape
      const { w, h } = baseDimensions[size][shape]

      const basicTilesPerView = (CONTAINER_WIDTH - 24) / (w + GAP)

      const slidesOffsetAfter = Math.floor(
        CONTAINER_WIDTH - (w + GAP) * Math.floor(basicTilesPerView),
      )
      const tilesPerView = Math.floor(basicTilesPerView * 1.0142 * 100) / 100

      const focusedTileScale = Math.min((w + 48) / w, (h + 20) / h)

      config[size][shape] = {
        w,
        h,
        tilesPerView,
        slidesOffsetAfter,
        focusedTileScale,
      }
    }
  }

  return config
}

export const tilesPerView = generateTilesPerView()

export const getTileProps = (
  tileShape = LineTileShape.ASPECT16x9,
  tileSize = LineTileSize.DEFAULT,
) => {
  return (
    tilesPerView[tileSize]?.[tileShape] ||
    tilesPerView[LineTileSize.DEFAULT][LineTileShape.ASPECT16x9]
  )
}
